/**
 * Подписи закрытых наборов агентского договора и заявки на выплату (AD-631,
 * AD-632) и проверки строк ответа по ним.
 *
 * Каждая таблица — `satisfies Record<союз из генерата, string>`: сервер
 * поменял набор статусов — `tsc` красный здесь. Таблицы названы `*_LABEL_KEYS`,
 * поэтому их значения судит `scan:i18n-keys` во всех пяти языках. Те же таблицы
 * служат множеством допустимых значений для проверки ответа сервера.
 */
import type {
  AgentContract,
  AgentContractStatus,
  AgentKycStatus,
  AgentPayoutRequest,
  AgentPayoutSchedule,
  AgentType,
  PayoutRequestStatus,
} from "../../types/api/agent-payouts";

export const AGENT_CONTRACT_STATUS_LABEL_KEYS = {
  draft: "referral.agentPayouts.contractStatus.draft",
  pending_signature: "referral.agentPayouts.contractStatus.pendingSignature",
  active: "referral.agentPayouts.contractStatus.active",
  suspended: "referral.agentPayouts.contractStatus.suspended",
  terminated: "referral.agentPayouts.contractStatus.terminated",
  expired: "referral.agentPayouts.contractStatus.expired",
} as const satisfies Record<AgentContractStatus, string>;

export const AGENT_KYC_STATUS_LABEL_KEYS = {
  not_started: "referral.agentPayouts.kycStatus.notStarted",
  pending: "referral.agentPayouts.kycStatus.pending",
  verified: "referral.agentPayouts.kycStatus.verified",
  rejected: "referral.agentPayouts.kycStatus.rejected",
} as const satisfies Record<AgentKycStatus, string>;

export const AGENT_PAYOUT_SCHEDULE_LABEL_KEYS = {
  ON_REQUEST: "referral.agentPayouts.schedule.onRequest",
  MONTHLY: "referral.agentPayouts.schedule.monthly",
  QUARTERLY: "referral.agentPayouts.schedule.quarterly",
} as const satisfies Record<AgentPayoutSchedule, string>;

export const AGENT_TYPE_LABEL_KEYS = {
  INDIVIDUAL: "referral.agentPayouts.agentType.individual",
  LEGAL_ENTITY: "referral.agentPayouts.agentType.legalEntity",
} as const satisfies Record<AgentType, string>;

export const PAYOUT_REQUEST_STATUS_LABEL_KEYS = {
  PENDING: "referral.agentPayouts.requestStatus.pending",
  AWAITING_TRANSFER: "referral.agentPayouts.requestStatus.awaitingTransfer",
  PAID: "referral.agentPayouts.requestStatus.paid",
  REJECTED: "referral.agentPayouts.requestStatus.rejected",
  FAILED: "referral.agentPayouts.requestStatus.failed",
} as const satisfies Record<PayoutRequestStatus, string>;

/**
 * Договор не завершён — тот же набор, что `AGENT_CONTRACT_OPEN_STATUSES` на
 * сервере: в нём можно менять реквизиты, KYC и расторгать.
 */
export const AGENT_CONTRACT_OPEN_STATUSES = {
  draft: true,
  pending_signature: true,
  active: true,
  suspended: true,
  terminated: false,
  expired: false,
} as const satisfies Record<AgentContractStatus, boolean>;

const hasKey = <K extends string>(
  table: Record<K, unknown>,
  value: unknown,
): value is K =>
  typeof value === "string" &&
  Object.prototype.hasOwnProperty.call(table, value);

export const isAgentContractStatus = (
  value: unknown,
): value is AgentContractStatus =>
  hasKey(AGENT_CONTRACT_STATUS_LABEL_KEYS, value);

export const isAgentKycStatus = (value: unknown): value is AgentKycStatus =>
  hasKey(AGENT_KYC_STATUS_LABEL_KEYS, value);

export const isAgentPayoutSchedule = (
  value: unknown,
): value is AgentPayoutSchedule =>
  hasKey(AGENT_PAYOUT_SCHEDULE_LABEL_KEYS, value);

export const isAgentType = (value: unknown): value is AgentType =>
  hasKey(AGENT_TYPE_LABEL_KEYS, value);

export const isPayoutRequestStatus = (
  value: unknown,
): value is PayoutRequestStatus =>
  hasKey(PAYOUT_REQUEST_STATUS_LABEL_KEYS, value);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Строка договора: проверены поля, по которым экран решает, что показать и
 * какие действия разрешить. Остальные поля читаются с проверкой наличия.
 */
export const isAgentContractRow = (value: unknown): value is AgentContract =>
  isRecord(value) &&
  typeof value["id"] === "string" &&
  typeof value["contractNumber"] === "string" &&
  isAgentContractStatus(value["status"]) &&
  isAgentKycStatus(value["kycStatus"]) &&
  typeof value["minPayoutAmount"] === "string";

/** Строка заявки: те же условия — идентификатор, сумма, статус. */
export const isAgentPayoutRequestRow = (
  value: unknown,
): value is AgentPayoutRequest =>
  isRecord(value) &&
  typeof value["id"] === "string" &&
  typeof value["amount"] === "string" &&
  isPayoutRequestStatus(value["status"]);
